/**
 * useLocalFilters - Hook for managing local filters in tiles
 *
 * This hook provides local filter management with automatic filter selection:
 * - If local filters are configured (non-empty), tile uses local filters
 * - If local filters are empty, tile uses global filters from GlobalFilterContext
 * - This selection is automatic - no manual mode toggle needed
 *
 * Key principle: Local and global filters are COMPLETELY INDEPENDENT.
 * The tile automatically chooses which to use based on whether local filters are configured.
 *
 * Usage:
 *   const { localFilters, updateLocalFilter, clearLocalFilters, api, hasLocalFilters, localFilterCount } = useLocalFilters(activeFile);
 *
 *   // Use api.post() and api.get() - automatically uses local or global filters
 *   const data = await api.post('/endpoint', { component_param: 'value' });
 *
 *   // Update local filters
 *   updateLocalFilter('objectTypes', ['Angebot', 'Auftrag']);
 *
 *   // hasLocalFilters tells you if tile is using local filters
 *   {hasLocalFilters && <LocalFiltersActiveBadge />}
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { useGlobalFilters } from '../stores/GlobalFilterContext/GlobalFilterContext';
import { createFilteredRequest, makePostRequest, makeRequest } from '../services/apiClient';

// Initial empty local filter state
export const createEmptyLocalFilters = () => ({
  objectTypes: [],
  dateRange: { startDate: null, endDate: null, enabled: false },
  eventAttributeFilters: [],
  activityFilters: { included: [], excluded: [], enabled: false },
  lifecycleActivityFilters: { included: [], excluded: [], enabled: false },
  leadTimeRange: { min: null, max: null, enabled: false },
  objectAttributePresenceFilters: { attributeNames: [], enabled: false, mode: 'include' },
  objectAttributeFilters: [],
  objectAttributeFilterSettings: { mode: 'include', matchType: 'all' },
  variantFilter: { objectType: null, variantActivitiesList: null, enabled: false },
  edgeDurationFilters: [],
  edgeContainmentFilters: { enabled: false, edges: [], matchType: 'any', mode: 'include' },
  activityNodeFilters: { enabled: false, nodes: [], matchType: 'any', mode: 'include' }
});

export const useLocalFilters = (activeFile = null, initialFilters = null) => {
  // Get global filters from context
  const { filterState: globalFilterState } = useGlobalFilters();

  // Local filter state - committed filters (used for API requests)
  const [localFilters, setLocalFilters] = useState(() =>
    initialFilters ? { ...createEmptyLocalFilters(), ...initialFilters } : createEmptyLocalFilters()
  );

  // Draft filter state - staging area for modal edits (not used until applied)
  const [draftFilters, setDraftFilters] = useState(null);

  // Track which file the current local filters belong to.
  // When activeFile changes we reset synchronously during render (derived-state pattern)
  // so that hasLocalFilters is already false in the same render pass.
  const [localFiltersFile, setLocalFiltersFile] = useState(activeFile);
  if (localFiltersFile !== activeFile) {
    setLocalFiltersFile(activeFile);
    setLocalFilters(createEmptyLocalFilters());
    setDraftFilters(null);
  }

  // Ref that always points to the current committed state — used to avoid stale closures
  const localFiltersRef = useRef(localFilters);
  localFiltersRef.current = localFilters;

  /**
   * Start editing local filters (enter draft mode)
   * Creates a draft copy of current local filters
   */
  const startDraftMode = useCallback(() => {
    setDraftFilters({ ...localFiltersRef.current });
  }, []); // stable — reads current value from ref, no dep on localFilters

  /**
   * Update a specific draft filter (while modal is open)
   * @param {string} filterType - The filter type (e.g., 'objectTypes', 'dateRange')
   * @param {any} value - The new value for the filter
   */
  const updateDraftFilter = useCallback((filterType, value) => {
    setDraftFilters(prev => {
      if (!prev) return prev; // Not in draft mode
      return {
        ...prev,
        [filterType]: value
      };
    });
  }, []);

  /**
   * Apply draft filters (commit changes when user clicks Apply)
   */
  const applyDraftFilters = useCallback(() => {
    if (draftFilters) {
      setLocalFilters(draftFilters);
      setDraftFilters(null); // Exit draft mode
    }
  }, [draftFilters]);

  /**
   * Cancel draft changes (discard when user closes modal without applying)
   */
  const cancelDraftFilters = useCallback(() => {
    setDraftFilters(null);
  }, []);

  /**
   * Update a specific local filter (DEPRECATED - use draft mode instead)
   * @param {string} filterType - The filter type (e.g., 'objectTypes', 'dateRange')
   * @param {any} value - The new value for the filter
   */
  const updateLocalFilter = useCallback((filterType, value) => {
    // If in draft mode, update draft; otherwise update committed filters
    if (draftFilters !== null) {
      updateDraftFilter(filterType, value);
    } else {
      setLocalFilters(prev => ({
        ...prev,
        [filterType]: value
      }));
    }
  }, [draftFilters, updateDraftFilter]);

  /**
   * Clear all local filters
   */
  const clearLocalFilters = useCallback(() => {
    setLocalFilters(createEmptyLocalFilters());
    setDraftFilters(null); // Also exit draft mode
  }, []);

  /**
   * Detect if any local filters are configured
   * This determines whether tile uses local or global filters
   */
  const hasLocalFilters = useMemo(() => {
    if (localFilters.objectTypes?.length > 0) return true;
    if (localFilters.dateRange?.enabled) return true;
    if (localFilters.eventAttributeFilters?.some(f => f.enabled)) return true;
    if (localFilters.activityFilters?.enabled) return true;
    if (localFilters.lifecycleActivityFilters?.enabled) return true;
    if (localFilters.leadTimeRange?.enabled) return true;
    if (localFilters.objectAttributePresenceFilters?.enabled) return true;
    if (localFilters.objectAttributeFilters?.some(f => f.enabled)) return true;
    if (localFilters.variantFilter?.enabled) return true;
    if (localFilters.edgeDurationFilters?.length > 0) return true;
    if (localFilters.edgeContainmentFilters?.enabled) return true;
    if (localFilters.activityNodeFilters?.enabled) return true;
    return false;
  }, [localFilters]);

  /**
   * Count active local filters (for badge display)
   */
  const localFilterCount = useMemo(() => {
    let count = 0;
    if (localFilters.objectTypes?.length > 0) count++;
    if (localFilters.dateRange?.enabled) count++;
    if (localFilters.eventAttributeFilters?.some(f => f.enabled)) count++;
    if (localFilters.activityFilters?.enabled) count++;
    if (localFilters.lifecycleActivityFilters?.enabled) count++;
    if (localFilters.leadTimeRange?.enabled) count++;
    if (localFilters.objectAttributePresenceFilters?.enabled) count++;
    if (localFilters.objectAttributeFilters?.some(f => f.enabled)) count++;
    if (localFilters.variantFilter?.enabled) count++;
    if (localFilters.edgeDurationFilters?.length > 0) count++;
    if (localFilters.edgeContainmentFilters?.enabled) count++;
    if (localFilters.activityNodeFilters?.enabled) count++;
    return count;
  }, [localFilters]);

  /**
   * Build filtered request - automatically chooses local or global filters
   * @param {object} requestData - Component-specific request data
   * @param {string} fileType - File type override
   */
  const buildFilteredRequest = useCallback((requestData = {}, fileType = null) => {
    const finalFileType = fileType || activeFile;
    if (!finalFileType) {
      // No silent fallback: requests without a dataset would resolve to the
      // server-global active file, which belongs to other users.
      throw new Error(
        'useLocalFilters: no file_type available — pass activeFile to useLocalFilters(activeFile) or a fileType override',
      );
    }

    const baseRequest = {
      file_type: finalFileType,
      ...requestData
    };

    // Automatic filter selection: use local if configured, otherwise use global
    const filterSource = hasLocalFilters ? localFilters : globalFilterState;
    const filterType = hasLocalFilters ? 'local' : 'global';

    console.log(`🔀 useLocalFilters - Auto-selecting ${filterType} filters:`, {
      hasLocalFilters,
      filterSource,
      originalRequest: requestData
    });

    // Use the same createFilteredRequest function for consistency
    const filteredRequest = createFilteredRequest(baseRequest, filterSource);

    console.log(`🔀 useLocalFilters - Final ${filterType} request:`, filteredRequest);

    return filteredRequest;
  }, [hasLocalFilters, localFilters, globalFilterState, activeFile]);

  /**
   * POST request with automatic filter selection (local or global)
   */
  const post = useCallback(async (endpoint, requestData = {}, options = {}, requestLabel = 'API request', fileType = null) => {
    const filteredRequest = buildFilteredRequest(requestData, fileType);
    return await makePostRequest(endpoint, filteredRequest, options, requestLabel);
  }, [buildFilteredRequest]);

  /**
   * GET request with automatic filter selection (local or global)
   */
  const get = useCallback(async (endpoint, requestData = {}, options = {}, requestLabel = 'API request', fileType = null) => {
    const filteredRequest = buildFilteredRequest(requestData, fileType);
    const configWithParams = {
      ...options,
      params: { ...options.params, ...filteredRequest }
    };
    return await makeRequest(endpoint, configWithParams, requestLabel);
  }, [buildFilteredRequest]);

  const api = useMemo(() => ({
    post,
    get,
    buildFilteredRequest
  }), [post, get, buildFilteredRequest]);

  // Current filters for useEffect dependencies (automatically chosen)
  const currentFilters = hasLocalFilters ? localFilters : globalFilterState;

  return {
    // Local filter management
    localFilters: draftFilters || localFilters, // Show draft if editing, otherwise committed
    updateLocalFilter,
    clearLocalFilters,

    // Draft mode management
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
    isInDraftMode: draftFilters !== null,

    // Filter status
    hasLocalFilters,
    localFilterCount,

    // API methods (same interface as useGlobalAPI) - now memoized
    api,

    // For useEffect dependencies
    currentFilters,
    globalFilterState,
    currentFile: activeFile
  };
};
